import { StoreProduct } from '@/types/menus.types';
import { StoreAndGroupType } from '@/context/context.types';

export interface ActiveBundleStoreGroupDetail {
  storeGroupId: string;
  bundleLabels: string[];
}

export interface ActiveBundleStoreGroupsResponse {
  variantId: string;
  storeGroups: ActiveBundleStoreGroupDetail[];
}

export const blockedCellKey = (variantId: string, storeGroupId: string) =>
  `${variantId.toLowerCase()}:${storeGroupId.toLowerCase()}`;

export const buildBlockedCellKeys = (responses: ActiveBundleStoreGroupsResponse[]): Set<string> => {
  const keys = new Set<string>();
  responses.forEach(({ variantId, storeGroups }) => {
    storeGroups.forEach(({ storeGroupId }) => {
      keys.add(blockedCellKey(String(variantId), String(storeGroupId)));
    });
  });
  return keys;
};

export const getActiveBundleLabelsForCell = (
  responses: ActiveBundleStoreGroupsResponse[],
  variantId: string,
  storeGroupId: string,
): string[] => {
  const normalizedVariantId = variantId.toLowerCase();
  const normalizedStoreGroupId = storeGroupId.toLowerCase();
  const match = responses.find((response) => String(response.variantId).toLowerCase() === normalizedVariantId);

  if (!match) {
    return [];
  }

  const storeGroup = match.storeGroups.find(
    (group) => String(group.storeGroupId).toLowerCase() === normalizedStoreGroupId,
  );

  return storeGroup?.bundleLabels ?? [];
};

export const getActiveBundleStoreGroupsForVariant = (
  responses: ActiveBundleStoreGroupsResponse[],
  variantId: string,
): ActiveBundleStoreGroupDetail[] => {
  const normalizedVariantId = variantId.toLowerCase();
  const match = responses.find((response) => String(response.variantId).toLowerCase() === normalizedVariantId);

  return match?.storeGroups ?? [];
};

export const getBlockedVariantIds = (responses: ActiveBundleStoreGroupsResponse[]): Set<string> => {
  return new Set(responses.map((response) => String(response.variantId).toLowerCase()));
};

export const isActiveChosenPriceRange = (chosenPriceRange: string | undefined | null): boolean => {
  if (!chosenPriceRange?.trim()) {
    return false;
  }

  if (chosenPriceRange === 'Inactive' || chosenPriceRange.startsWith('Inactive-')) {
    return false;
  }

  return true;
};

export const isBlockableChosenPriceRange = (chosenPriceRange: string | undefined | null): boolean => {
  if (!chosenPriceRange?.trim()) {
    return false;
  }

  return chosenPriceRange !== 'Inactive';
};

export interface StoreGroupCell {
  variantId: string;
  storeGroupId: string;
  hasPrice: boolean;
}

interface PriceCell {
  variantId: string;
  storeGroupId: string;
}

export const collectStoreGroupCells = (
  storeProducts: StoreProduct[],
  storeGroupNames: string[],
  resolveStoreGroupId: (storeGroupName: string, product: StoreProduct) => string | undefined,
): StoreGroupCell[] => {
  const cells: StoreGroupCell[] = [];

  storeProducts.forEach((product) => {
    const variantId = product.variantId;

    if (!variantId) {
      return;
    }

    storeGroupNames.forEach((storeGroupName) => {
      const storeGroupData = product.storeGroups?.find((sg) => sg.storeGroupName === storeGroupName);
      const storeGroupId = storeGroupData?.storeGroupId ?? resolveStoreGroupId(storeGroupName, product);

      if (!storeGroupId) {
        return;
      }

      cells.push({
        variantId,
        storeGroupId,
        hasPrice: isBlockableChosenPriceRange(storeGroupData?.chosenPriceRange),
      });
    });
  });

  return cells;
};

export const isCellBlockedByActiveBundles = (cell: StoreGroupCell, blockedCellKeys: Set<string>): boolean =>
  cell.hasPrice && blockedCellKeys.has(blockedCellKey(cell.variantId, cell.storeGroupId));

export const isCellSubmitEligible = (cell: StoreGroupCell, blockedCellKeys: Set<string>): boolean =>
  cell.hasPrice && !blockedCellKeys.has(blockedCellKey(cell.variantId, cell.storeGroupId));

export const partitionStoreGroupCellsByActiveBundles = (cells: StoreGroupCell[], blockedCellKeys: Set<string>) => {
  const blockedCells = cells.filter((cell) => isCellBlockedByActiveBundles(cell, blockedCellKeys));
  const eligibleCells = cells.filter((cell) => isCellSubmitEligible(cell, blockedCellKeys));

  return { blockedCells, eligibleCells };
};

export const getEligibleVariantIdsFromCells = (cells: StoreGroupCell[], blockedCellKeys: Set<string>): string[] => {
  const variantIds = new Set<string>();

  cells.forEach((cell) => {
    if (isCellSubmitEligible(cell, blockedCellKeys)) {
      variantIds.add(cell.variantId);
    }
  });

  return Array.from(variantIds);
};

export const getStoreNumbersInStoreGroups = (
  storeGroupIds: Set<string>,
  storeProducts: StoreProduct[],
  storesAndGroups?: StoreAndGroupType | null,
): Set<string> => {
  const normalizedIds = new Set(Array.from(storeGroupIds).map((id) => id.toLowerCase()));
  const storeNumbers = new Set<string>();

  storeProducts.forEach((product) => {
    product.storeGroups?.forEach((storeGroup) => {
      if (storeGroup.storeGroupId && normalizedIds.has(storeGroup.storeGroupId.toLowerCase())) {
        storeGroup.stores?.forEach((store) => storeNumbers.add(store));
      }
    });
  });

  if (storesAndGroups?.origin === 'tab-stores-groups') {
    storesAndGroups.storeGroups?.forEach((storeGroup) => {
      if (storeGroup.storeGroupId && normalizedIds.has(storeGroup.storeGroupId.toLowerCase())) {
        storeGroup.stores?.forEach((store) => storeNumbers.add(store));
      }
    });
  }

  if (storesAndGroups?.origin === 'tab-store') {
    storesAndGroups.stores?.forEach((store) => {
      const storeGroupId = storesAndGroups.storeGroups?.find(
        (group) => group.storeGroup === store.storeGroup,
      )?.storeGroupId;

      if (storeGroupId && normalizedIds.has(storeGroupId.toLowerCase())) {
        storeNumbers.add(store.storeNo);
      }
    });
  }

  return storeNumbers;
};

export const buildRemoveRequestPayload = (
  selectedStores: string[],
  storeGroupCells: StoreGroupCell[],
  _blockedCellKeys: Set<string>,
  storeProducts: StoreProduct[],
  storesAndGroups?: StoreAndGroupType | null,
): { stores: string[]; variantIds: string[] } => {
  const dedupedSelectedStores = Array.from(new Set(selectedStores));

  if (storeGroupCells.length === 0) {
    return {
      stores: dedupedSelectedStores,
      variantIds: storeProducts.map((product) => product.variantId).filter(Boolean) as string[],
    };
  }

  const variantIds = Array.from(new Set(storeGroupCells.filter((cell) => cell.hasPrice).map((cell) => cell.variantId)));
  const normalizedVariantIds = new Set(variantIds.map((variantId) => variantId.toLowerCase()));

  // Only store groups where a submitted variant currently has a price are deactivated,
  // so stores that are already inactive must stay out of the request.
  const pricedStoreGroupIds = new Set<string>();
  storeProducts.forEach((product) => {
    if (!product.variantId || !normalizedVariantIds.has(product.variantId.toLowerCase())) {
      return;
    }

    product.storeGroups?.forEach((storeGroup) => {
      if (storeGroup.storeGroupId && isBlockableChosenPriceRange(storeGroup.chosenPriceRange)) {
        pricedStoreGroupIds.add(storeGroup.storeGroupId);
      }
    });
  });

  const pricedStores = getStoreNumbersInStoreGroups(pricedStoreGroupIds, storeProducts, storesAndGroups);

  return {
    stores: pricedStores.size > 0 ? dedupedSelectedStores.filter((store) => pricedStores.has(store)) : [],
    variantIds,
  };
};

/** @deprecated Use collectStoreGroupCells instead */
export const collectPriceCells = (
  storeProducts: StoreProduct[],
  storeGroupNames: string[],
  resolveStoreGroupId: (storeGroupName: string, product: StoreProduct) => string | undefined,
): PriceCell[] => {
  return collectStoreGroupCells(storeProducts, storeGroupNames, resolveStoreGroupId)
    .filter((cell) => cell.hasPrice)
    .map(({ variantId, storeGroupId }) => ({ variantId, storeGroupId }));
};

/** @deprecated Use partitionStoreGroupCellsByActiveBundles instead */
export const partitionPriceCellsByActiveBundles = (priceCells: PriceCell[], blockedCellKeys: Set<string>) => {
  const cells: StoreGroupCell[] = priceCells.map(({ variantId, storeGroupId }) => ({
    variantId,
    storeGroupId,
    hasPrice: true,
  }));

  return partitionStoreGroupCellsByActiveBundles(cells, blockedCellKeys);
};

/** @deprecated Use cell-level partitionPriceCellsByActiveBundles instead */
export const partitionProductsByActiveBundles = (storeProducts: StoreProduct[], blockedVariantIds: Set<string>) => {
  const blockedProducts = storeProducts.filter(
    (product) => product.variantId && blockedVariantIds.has(product.variantId.toLowerCase()),
  );
  const eligibleProducts = storeProducts.filter(
    (product) => product.variantId && !blockedVariantIds.has(product.variantId.toLowerCase()),
  );

  return { blockedProducts, eligibleProducts };
};
